import { useCallback, useRef } from 'react'

import { type Query, useQuery } from '@tanstack/react-query'

import { useAuthInfo, useOrgHelper } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import { convertDatesInObject } from '@/helpers/api'
import { SweetspotError } from '@/helpers/error'

import {
  type BillingOrgStatus,
  billingOrganizationQueryKey,
} from '@/hooks/billing/types'

import type { BillingOrganization } from '@/types/BillingOrganization'

// TODO: change to a specific status code
export const NO_BILLING_ORG_EXISTS_STATUS_CODE: number = 404

interface UseBillingOrganizationOptions<T> {
  enabled?: boolean
  refetchOnMount?: boolean
  refetchOnWindowFocus?: boolean
  select?: (data: BillingOrganization | null) => T
}

const fetchBillingOrganization = async (
  accessToken: string | undefined | null,
): Promise<Response | null> => {
  if (accessToken === undefined) {
    throw new Error('No access token provided.')
  }
  if (accessToken === null) {
    return null
  }
  const url = `${API_URL}/api/v1/billing-organization/`
  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  return res
}

export const useBillingOrganization = <T = BillingOrganization | null>({
  enabled = true,
  refetchOnMount = false,
  refetchOnWindowFocus = false,
  select,
}: UseBillingOrganizationOptions<T> = {}) => {
  const { accessToken } = useAuthInfo()
  const retryCount = useRef(0)

  const selectFn = useCallback(
    (data: BillingOrganization | null): T => {
      if (select) {
        return select(data)
      }
      return data as unknown as T
    },
    [select],
  )

  const query = useQuery<BillingOrganization | null, Error, T>({
    queryFn: async () => {
      const res: Response | null = await fetchBillingOrganization(accessToken)
      if (res === null) {
        return res
      }
      if (!res.ok) {
        if (res.status === 404) {
          // this is expected for new users
          return null
        }
        throw new SweetspotError({
          name: 'FETCH_ERROR',
          message: res.statusText,
          cause: {
            status: res.status,
          },
        })
      }
      return convertDatesInObject(await res.json())
    },
    queryKey: billingOrganizationQueryKey.all(),
    retry: (count, error) => {
      retryCount.current = count
      return true
    },
    enabled: enabled && accessToken !== undefined && accessToken !== null,
    refetchOnMount: refetchOnMount,
    refetchOnWindowFocus: refetchOnWindowFocus,
    staleTime: 5 * 60 * 1000, // 5 minutes,
    refetchInterval: 60 * 60 * 1000, // 60 minutes,
    select: selectFn,
  })

  return { retryCount: retryCount.current, ...query }
}

export const useBillingOrganizationWithStatus = () => {
  const { accessToken } = useAuthInfo()
  const { orgHelper, loading: orgHelperIsLoading } = useOrgHelper()

  const retryCount = useRef(0)

  const selectFn = useCallback(
    (data: BillingOrganization | null): BillingOrgStatus => {
      const hasPropelAuthOrg = orgHelper && orgHelper.getOrgIds().length > 0
      const isMaxRetry = (hasPropelAuthOrg && retryCount.current >= 3) || false
      if (data) {
        return { status: 'in_billing_org', billingOrg: data }
      }
      if (hasPropelAuthOrg) {
        if (isMaxRetry) {
          return { status: 'onboarding_required' }
        }
        return { status: 'pending', retryCount: retryCount.current }
      }
      if (orgHelperIsLoading) {
        return { status: 'pending', retryCount: retryCount.current }
      }
      return { status: 'onboarding_required' }
    },
    [retryCount.current, orgHelperIsLoading, orgHelper?.getOrgIds],
  )

  const refetchInterval = useCallback(
    (query: Query<BillingOrganization | null>) => {
      if (query.state.data !== undefined) {
        // if the user has a billing org in propelauth but the webhooks haven't updated
        // the billing org yet in our db yet, then we should continue refetching
        if (
          query.state.data === null &&
          orgHelper &&
          orgHelper.getOrgIds().length > 0
        ) {
          return 2000
        }
        return false
      }
      return 2000
    },
    [orgHelperIsLoading, orgHelper?.getOrgIds],
  )

  const retry = useCallback(
    (count: number, error: Error) => {
      retryCount.current = count
      const hasPropelAuthOrg =
        !orgHelperIsLoading && orgHelper && orgHelper.getOrgIds().length > 0
      if (hasPropelAuthOrg) {
        return count < 5
      }
      return false
    },
    [retryCount, orgHelperIsLoading, orgHelper?.getOrgIds],
  )

  const queryFn = useCallback(async () => {
    const res: Response | null = await fetchBillingOrganization(accessToken)
    if (res === null || res.status === 404) {
      return null
    }
    if (!res.ok) {
      throw new SweetspotError({
        name: 'FETCH_ERROR',
        message: res.statusText,
        cause: {
          status: res.status,
        },
      })
    }
    return convertDatesInObject(await res.json()) as BillingOrganization
  }, [accessToken])

  return useQuery({
    queryFn,
    refetchInterval,
    retry,
    select: selectFn,
    queryKey: billingOrganizationQueryKey.all(),
    enabled: accessToken !== undefined && accessToken !== null,
    staleTime: 5 * 60 * 1000, // 5 minutes,
  })
}

export const useCanInviteMembers = () => {
  const { data: billingOrganization } = useBillingOrganization()
  return !!billingOrganization && billingOrganization.tier !== 'free'
}

export const useMaximumUsersReached = () => {
  const { data: billingOrganization } = useBillingOrganization()
  return (
    !!billingOrganization &&
    billingOrganization.users.length >= billingOrganization.num_seats
  )
}

const selectIsPaidUser = (billingOrganization: BillingOrganization | null) => {
  if (!billingOrganization) {
    return false
  }
  return billingOrganization.tier !== 'free'
}

export const useIsPaidUser = () => {
  return useBillingOrganization({ select: selectIsPaidUser })
}
